import { useEffect, useRef, useState } from 'react';
import {
  PAYOR_ENROLLMENT_STATUSES,
  type PayorAcceptingUser,
  type PayorEnrollmentStatus,
  type PayorUsers as PayorUsersData,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';
import { Dd, type DdOption } from './Dropdown.js';
import { DateField } from './DateTimeField.js';
import { Modal } from './Modal.js';

/**
 * ehr#611 - "Users accepting this insurance" on a payor (Data & settings > Payors, the payor
 * pop-up). The users enrolled with the payor: the same rows as each Provider Profile's Payor
 * enrollments (GET / POST / PUT / DELETE /payors/:id/users). Add user picks a provider (active users
 * who see clients) not already listed; the trash button asks first and removes that one enrollment,
 * never the user's account. Changes save right away, apart from the pop-up's Save.
 *
 * ehr#611 (Chase 10-23): the Add user list floats over the pop-up (Dd `floating`: pinned to the
 * window, upward when there isn't room below), so the pop-up's footer no longer cuts it off. Picking
 * a user opens the enrollment pop-up, the Provider Profile's "Add a payor enrollment" (status,
 * effective date, provider ID with this payor, note) with this payor and that user fixed; Save adds
 * the row with those fields. Each row's Edit opens the same pop-up to change them.
 *
 * ehr#611 (Chase 10-25): a payor opened to view gives each row a View button. It opens the same
 * pop-up read-only (status, effective date, provider ID, note) with Close only; nothing saves.
 */

const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

/** Add user's picker: the providers not on the list yet (no duplicates). */
export function addableUsers(data: PayorUsersData | null) {
  const listed = new Set((data?.users ?? []).map((u) => u.userId));
  return (data?.candidates ?? [])
    .filter((c) => !listed.has(c.id))
    .map((c) => ({ value: c.id, label: c.label }));
}

/**
 * What the enrollment pop-up edits: a new row for the picked user, or a listed user's row.
 * 'view' shows a listed row read-only (a payor opened to view).
 */
export interface EnrollmentDraft {
  mode: 'add' | 'edit' | 'view';
  userId: string;
  label: string;
  status: PayorEnrollmentStatus;
  effective: string;
  pid: string;
  note: string;
}

/** Add user: the picked user, Enrolled (they accept this insurance), the rest blank. */
export function newEnrollment(pick: { value: string; label: string }): EnrollmentDraft {
  return {
    mode: 'add',
    userId: pick.value,
    label: pick.label,
    status: 'Enrolled',
    effective: '',
    pid: '',
    note: '',
  };
}

/** Edit (or View): the row as saved. */
export function rowEnrollment(
  u: PayorAcceptingUser,
  mode: 'edit' | 'view' = 'edit',
): EnrollmentDraft {
  return {
    mode,
    userId: u.userId,
    label: u.label,
    status: u.status,
    effective: u.effective ?? '',
    pid: u.pid ?? '',
    note: u.note ?? '',
  };
}

/** The Provider Profile's rule (rowProblems): an Enrolled payor needs its effective date. */
export function enrollmentProblems(d: EnrollmentDraft): { effective?: string } {
  return d.status === 'Enrolled' && !d.effective.trim()
    ? { effective: 'Add the effective date. Enrolled payors need one.' }
    : {};
}

/** The POST body (with the user) or the PUT body. */
export function enrollmentBody(d: EnrollmentDraft) {
  const fields = {
    status: d.status,
    effective: d.effective.trim(),
    pid: d.pid.trim(),
    note: d.note.trim(),
  };
  return d.mode === 'add' ? { userId: d.userId, ...fields } : fields;
}

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${m}/${d}/${y}` : '';
};

/** The line under a user's name: effective date, provider ID and note, when set. */
export function rowMeta(u: PayorAcceptingUser): string {
  return [
    u.effective ? `Effective ${fmtDate(u.effective)}` : '',
    u.pid ? `ID ${u.pid}` : '',
    u.note ?? '',
  ]
    .filter(Boolean)
    .join(' · ');
}

const STATUS_OPTIONS: DdOption[] = PAYOR_ENROLLMENT_STATUSES.map((s) => ({ value: s, label: s }));

/** The pop-up's title for each mode. */
export function enrollmentTitle(mode: EnrollmentDraft['mode']): string {
  return mode === 'add'
    ? 'Add a payor enrollment'
    : mode === 'edit'
      ? 'Edit payor enrollment'
      : 'Payor enrollment';
}

/** The enrollment pop-up (the Provider Profile's payor enrollment drawer, for one payor). */
function EnrollmentPopup({
  draft,
  payorId,
  payorName,
  busy,
  error,
  onChange,
  onSave,
  onClose,
}: {
  draft: EnrollmentDraft;
  payorId: string;
  payorName: string;
  busy: boolean;
  error: string;
  onChange: (d: EnrollmentDraft) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const [errs, setErrs] = useState<{ effective?: string }>({});
  const set = <K extends keyof EnrollmentDraft>(k: K, v: EnrollmentDraft[K]) => {
    if (k === 'effective' || k === 'status') setErrs({});
    onChange({ ...draft, [k]: v });
  };
  const save = () => {
    const e = enrollmentProblems(draft);
    setErrs(e);
    if (!Object.keys(e).length) onSave();
  };
  const close = () => {
    if (!busy) onClose();
  };
  const viewing = draft.mode === 'view';
  const locked = busy || viewing;
  return (
    <Modal
      title={enrollmentTitle(draft.mode)}
      onClose={close}
      className={viewing ? 'py-enroll-modal py-enroll-view' : 'py-enroll-modal'}
      actions={
        viewing ? (
          <button type="button" className="btn primary" onClick={close}>
            Close
          </button>
        ) : (
          <>
            <button type="button" className="btn ghost" onClick={close} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save payor'}
            </button>
          </>
        )
      }
    >
      <p className="py-enroll-hint">
        {draft.mode === 'add'
          ? `${draft.label} will accept ${payorName}. It also shows on their Provider Profile.`
          : viewing
            ? 'View only. To change it, click Edit on the payor.'
            : "Changes also show on this user's Provider Profile."}
      </p>
      <div className="f py-enroll-user">
        <label>User</label>
        <Dd
          value={draft.userId}
          onChange={() => undefined}
          options={[{ value: draft.userId, label: draft.label }]}
          ariaLabel="User"
          searchable={false}
          disabled
        />
      </div>
      <div className="f py-enroll-payor">
        <label>Payor</label>
        <Dd
          value={payorId}
          onChange={() => undefined}
          options={[{ value: payorId, label: payorName }]}
          ariaLabel="Payor"
          searchable={false}
          disabled
        />
      </div>
      <div className="f py-enroll-status">
        <label>Enrollment status</label>
        <Dd
          value={draft.status}
          onChange={(v) => set('status', v as PayorEnrollmentStatus)}
          options={STATUS_OPTIONS}
          ariaLabel="Enrollment status"
          searchable={false}
          disabled={locked}
          floating
        />
      </div>
      <div className="f py-enroll-date">
        <label>Effective date</label>
        <DateField
          value={draft.effective}
          onChange={(v) => set('effective', v)}
          min="1970-01-01"
          ariaLabel="Effective date"
          disabled={locked}
        />
        {errs.effective && (
          <span className="payors-err" role="alert">
            {errs.effective}
          </span>
        )}
      </div>
      <div className="f">
        <label htmlFor="pyEnrPid">Provider ID with this payor</label>
        <input
          id="pyEnrPid"
          value={draft.pid}
          onChange={(e) => set('pid', e.target.value)}
          maxLength={120}
          autoComplete="off"
          disabled={busy}
          readOnly={viewing}
        />
        {!viewing && <span className="help">The ID this payor gave the provider, if any.</span>}
      </div>
      <div className="f">
        <label htmlFor="pyEnrNote">Note</label>
        <input
          id="pyEnrNote"
          value={draft.note}
          onChange={(e) => set('note', e.target.value)}
          maxLength={500}
          autoComplete="off"
          disabled={busy}
          readOnly={viewing}
        />
        {!viewing && (
          <span className="help">Shown under the status, such as the date it was submitted.</span>
        )}
      </div>
      {error && (
        <p className="payors-err" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/**
 * ehr#611 (Chase 10-24): Add user, the pencil and the trash show only while the payor is being
 * edited by someone who may change payors. A payor opened to view (Close / Edit) shows the list
 * alone; Edit brings the controls back. The server still refuses changes from other roles.
 */
export function usersEditable(canManagePayors: boolean, viewOnly: boolean): boolean {
  return canManagePayors && !viewOnly;
}

export function PayorUsers({
  payorId,
  payorName,
  canEdit,
}: {
  payorId: string;
  payorName: string;
  canEdit: boolean;
}) {
  const [data, setData] = useState<PayorUsersData | null>(null);
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pop, setPop] = useState<EnrollmentDraft | null>(null);
  const [popErr, setPopErr] = useState('');
  const addRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setErr('');
    setPop(null);
    api
      .get<PayorUsersData>(`/payors/${encodeURIComponent(payorId)}/users`)
      .then((r) => live && setData(r))
      .catch((e: unknown) => live && setErr(msgOf(e)));
    return () => {
      live = false;
    };
  }, [payorId]);

  // Add user opens the picker straight away
  useEffect(() => {
    if (adding) addRef.current?.querySelector<HTMLButtonElement>('.dd-trigger')?.click();
  }, [adding]);

  const options = addableUsers(data);

  const openPop = (d: EnrollmentDraft) => {
    setPopErr('');
    setPop(d);
  };

  // Picking a user opens the enrollment pop-up; nothing is saved until its Save.
  const pick = (userId: string) => {
    const o = options.find((x) => x.value === userId);
    if (o && !busy) openPop(newEnrollment(o));
  };

  const savePop = () => {
    if (!pop || busy) return;
    const base = `/payors/${encodeURIComponent(payorId)}/users`;
    const req =
      pop.mode === 'add'
        ? api.post<PayorUsersData>(base, enrollmentBody(pop))
        : api.put<PayorUsersData>(`${base}/${encodeURIComponent(pop.userId)}`, enrollmentBody(pop));
    setBusy(true);
    setPopErr('');
    req
      .then((r) => {
        setData(r);
        if (pop.mode === 'add') setAdding(false);
        setPop(null);
      })
      .catch((e: unknown) => setPopErr(msgOf(e)))
      .finally(() => setBusy(false));
  };

  const remove = async (u: PayorAcceptingUser) => {
    if (busy) return;
    const ok = await askConfirm({
      message: `Remove ${u.label} from ${payorName}? It comes off their Payor enrollments. Their account stays as it is.`,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    setBusy(true);
    setErr('');
    api
      .del<PayorUsersData>(
        `/payors/${encodeURIComponent(payorId)}/users/${encodeURIComponent(u.userId)}`,
      )
      .then(setData)
      .catch((e: unknown) => setErr(msgOf(e)))
      .finally(() => setBusy(false));
  };

  return (
    <section className="py-users" aria-label="Users accepting this insurance">
      <div className="py-users-head">
        <h4>Users accepting this insurance</h4>
        {canEdit && (
          <button
            type="button"
            className="btn py-users-addbtn"
            onClick={() => setAdding((a) => !a)}
            disabled={!data || busy}
            aria-expanded={adding}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              aria-hidden
            >
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            Add user
          </button>
        )}
      </div>
      {adding && canEdit && (
        <div className="py-users-add" ref={addRef}>
          <Dd
            value=""
            onChange={pick}
            options={options}
            ariaLabel="User to add"
            placeholder={options.length ? 'Pick a user' : 'Everyone is on the list'}
            disabled={busy || !options.length}
            floating
          />
          <button type="button" className="btn ghost" onClick={() => setAdding(false)}>
            Cancel
          </button>
        </div>
      )}
      {err && (
        <p className="payors-err" role="alert">
          {err}
        </p>
      )}
      {!data && !err ? (
        <p className="py-users-empty">Loading users…</p>
      ) : data && !data.users.length ? (
        <p className="py-users-empty">
          No users yet.
          {canEdit ? ' Add the providers who take this insurance.' : ''}
        </p>
      ) : data ? (
        <ul className="py-users-list">
          {data.users.map((u) => {
            const meta = rowMeta(u);
            return (
              <li key={u.enrollmentId} className="py-user">
                <span className="py-av" aria-hidden>
                  {u.initials}
                </span>
                <span className="py-user-name">
                  {u.label}
                  {meta && <span className="py-user-meta">{meta}</span>}
                </span>
                {u.status !== 'Enrolled' && <span className="py-user-st">{u.status}</span>}
                {!canEdit && (
                  <button
                    type="button"
                    className="btn sm py-user-view"
                    aria-label={`View ${u.label}`}
                    title="View status, date, provider ID and note"
                    onClick={() => openPop(rowEnrollment(u, 'view'))}
                  >
                    View
                  </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="py-user-rm py-user-edit"
                    aria-label={`Edit ${u.label}`}
                    title="Edit status, date, provider ID and note"
                    onClick={() => openPop(rowEnrollment(u))}
                    disabled={busy}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="py-user-rm"
                    aria-label={`Remove ${u.label}`}
                    title="Remove from this payor"
                    onClick={() => void remove(u)}
                    disabled={busy}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" />
                    </svg>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {pop && (
        <EnrollmentPopup
          draft={pop}
          payorId={payorId}
          payorName={payorName}
          busy={busy}
          error={popErr}
          onChange={setPop}
          onSave={savePop}
          onClose={() => setPop(null)}
        />
      )}
    </section>
  );
}
